<template>
    <Modal
        ref="modalRef"
        :primary-button-disabled="!form.isDirty"
        :primary-button-label="!owner ? trans('general.action.add') : trans('general.action.save')"
        :processing="form.processing"
        :title="title"
        max-width="4xl"
        @submit="submitForm"
    >
        <form @submit.prevent="submitForm">
            <div class="flex flex-col divide-y divide-gray-200">
                <section class="pb-6 first:pt-0">
                    <row class="g-4">
                        <column md="4">
                            <div class="text-base leading-snug font-semibold text-body">
                                {{ trans('owners.form.name.label') }}
                                <span class="text-danger">*</span>
                            </div>
                            <p class="mb-0 text-sm leading-6 text-muted">
                                {{ trans('owners.form.name.placeholder') }}
                            </p>
                        </column>
                        <column md="8">
                            <div class="rounded-card border border-panel-border bg-panel p-4">
                                <jet-input
                                    v-model="form.name"
                                    :autofocus="true"
                                    :horizontal="false"
                                    :invalidFeedback="form.errors.name"
                                    :label="trans('owners.form.name.label')"
                                    :placeholder="trans('owners.form.name.placeholder')"
                                    :required="true"
                                    class="mb-0"
                                >
                                    <template #prepend>
                                        <icon name="cil-building" />
                                    </template>
                                </jet-input>
                            </div>
                        </column>
                    </row>
                </section>

                <section class="py-6">
                    <row class="g-4">
                        <column md="4">
                            <div class="text-base leading-snug font-semibold text-body">
                                {{ trans('owners.form.contact_details.label') }}
                            </div>
                            <p class="mb-0 text-sm leading-6 text-muted">
                                {{ trans('owners.form.contact_details.description') }}
                            </p>
                            <jet-button class="mt-3" type="button" variant="secondary" @click="addContact">
                                {{ trans('stakeholders.form.email.add.label') }}
                                <icon icon="cil-plus" />
                            </jet-button>
                        </column>
                        <column md="8">
                            <div class="flex flex-col gap-3">
                                <div v-for="(contact, index) in form.contacts" :key="contact.localKey">
                                    <StakeholderContactCard
                                        :email="contact.email"
                                        :errors="{
                                            name: contactErrors[contact.localKey]?.name,
                                            role: contactErrors[contact.localKey]?.role,
                                            email: contactErrors[contact.localKey]?.email,
                                            phone: contactErrors[contact.localKey]?.phone,
                                        }"
                                        :required="{
                                            name: false,
                                            role: false,
                                            email: true,
                                            phone: false,
                                        }"
                                        :is-informational="contact.isInformational"
                                        :informational-tooltip="trans('stakeholders.form.support_contacts.informational.tooltip')"
                                        :name="contact.name"
                                        :phone="contact.phone"
                                        :placeholders="contactCardPlaceholders"
                                        :show-informational-checkbox="true"
                                        :role="contact.role"
                                        @update:email="form.contacts[index].email = $event"
                                        @update:isInformational="form.contacts[index].isInformational = $event"
                                        @update:name="form.contacts[index].name = $event"
                                        @update:phone="form.contacts[index].phone = $event"
                                        @update:role="form.contacts[index].role = $event"
                                    >
                                        <template #actions>
                                            <jet-button
                                                v-c-tooltip="{ content: trans('general.action.delete') }"
                                                :destructive="true"
                                                type="button"
                                                variant="secondary"
                                                class="min-w-0"
                                                @click="removeContact(index)"
                                            >
                                                <icon name="cil-trash" />
                                            </jet-button>
                                        </template>
                                    </StakeholderContactCard>
                                </div>
                                <div
                                    v-if="form.contacts.length === 0"
                                    class="rounded-card border border-panel-border bg-panel p-4 text-sm text-muted"
                                >
                                    {{ trans('stakeholders.form.contact_details.empty') }}
                                </div>
                            </div>
                        </column>
                    </row>
                </section>

                <section class="py-6">
                    <row class="g-4">
                        <column md="4">
                            <div class="text-base leading-snug font-semibold text-body">
                                {{ trans('stakeholders.form.description.label') }}
                            </div>
                            <p class="mb-0 text-sm leading-6 text-muted">
                                {{ trans('stakeholders.form.description.placeholder') }}
                            </p>
                        </column>
                        <column md="8">
                            <div class="rounded-card border border-panel-border bg-panel p-4">
                                <jet-input
                                    v-model="form.description"
                                    :horizontal="false"
                                    :invalidFeedback="form.errors.description"
                                    :label="trans('stakeholders.form.description.label')"
                                    :placeholder="trans('stakeholders.form.description.placeholder')"
                                    class="mb-0"
                                >
                                    <template #prepend>
                                        <icon name="cil-briefcase" />
                                    </template>
                                </jet-input>
                            </div>
                        </column>
                    </row>
                </section>

                <LanguageSelectSection v-model="form.language" :error="form.errors.language" class="pt-6" />
            </div>
        </form>
        <row class="g-4 mt-4">
            <column md="4" />
            <column md="8">
                <required-fields />
            </column>
        </row>
    </Modal>
</template>

<script setup>
import Modal from '@/Components/Modal.vue';
import RequiredFields from '@/Components/RequiredFields.vue';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import { trans } from '@/mixins/i18n.js';
import LanguageSelectSection from '@/Pages/Stakeholders/Components/LanguageSelectSection.vue';
import StakeholderContactCard from '@/Pages/Stakeholders/Components/StakeholderContactCard.vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const modalRef = ref(null);
const emit = defineEmits(['created', 'updated']);

const props = defineProps({
    owner: Object,
});

const page = usePage();

const contactCardPlaceholders = {
    name: trans('stakeholders.form.contact_name.placeholder'),
    role: trans('stakeholders.form.description.placeholder'),
    email: trans('stakeholders.form.email.placeholder'),
    phone: trans('stakeholders.form.phone.placeholder'),
};

let contactKeyCounter = 0;

const createContactLocalKey = () => `contact-${contactKeyCounter++}`;

const emptyContact = () => ({
    id: null,
    name: '',
    role: '',
    email: '',
    phone: '',
    isInformational: false,
    localKey: createContactLocalKey(),
});

const contacts = props.owner?.contacts?.length
    ? props.owner.contacts.map((contact) => ({
          id: contact.id,
          name: contact.name,
          role: contact.role ?? '',
          email: contact.email ?? '',
          phone: contact.phone ?? '',
          isInformational: contact.isInformational,
          localKey: createContactLocalKey(),
      }))
    : [];

const form = useForm({
    name: props.owner?.name ?? '',
    description: props.owner?.description ?? '',
    contacts,
    language: props.owner?.language || page.props.locale,
});

const contactErrors = ref({});

const title = computed(() => {
    return props.owner ? trans('owners.edit.title') : trans('owners.create.title');
});

const addContact = () => {
    form.contacts.push(emptyContact());
};

const removeContact = (index) => {
    form.contacts.splice(index, 1);
};

const mapContactErrors = (errors, field, localKeys) => {
    return localKeys.reduce((carry, localKey, index) => {
        const mappedErrors = {
            name: errors[`${field}.${index}.name`],
            role: errors[`${field}.${index}.role`],
            email: errors[`${field}.${index}.email`],
            phone: errors[`${field}.${index}.phone`],
        };

        if (Object.values(mappedErrors).some(Boolean)) {
            carry[localKey] = mappedErrors;
        }

        return carry;
    }, {});
};

const submitForm = () => {
    const url = props.owner ? route('owners.update', { owner: props.owner }) : route('owners.store');

    const submittedContactKeys = form.contacts.map((contact) => contact.localKey);
    contactErrors.value = {};
    form.clearErrors();

    form.transform((data) => {
        const transformed = {
            name: data.name,
            description: data.description,
            language: data.language,
            contacts: data.contacts.map(({ localKey, ...contact }) => contact),
        };

        if (props.owner) {
            transformed._method = 'PUT';
        }

        return transformed;
    }).post(url, {
        preserveScroll: true,
        onError: (errors) => {
            contactErrors.value = mapContactErrors(errors, 'contacts', submittedContactKeys);
        },
        onSuccess: () => {
            emit(props.owner ? 'updated' : 'created', props.owner);
            modalRef.value?.close();
        },
    });
};
</script>
